import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, Bell, X, CheckCircle, TrendingUp, 
  Shield, Target, Zap, Eye, EyeOff
} from 'lucide-react';
import { useEnhancedCredits } from '@/hooks/useEnhancedCredits';
import { formatDistanceToNow } from 'date-fns';

export const SmartAlertsPanel: React.FC = () => {
  const { 
    smartAlerts, 
    isLoadingAlerts,
    markAlertAsRead,
    dismissAlert
  } = useEnhancedCredits();

  if (isLoadingAlerts) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Smart Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!smartAlerts || !Array.isArray(smartAlerts) || smartAlerts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Bell className="h-5 w-5" />
            <span>Smart Alerts</span>
            <Badge variant="outline" className="ml-auto">
              All Clear
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-success opacity-50" />
            <p className="font-medium">No active alerts</p>
            <p className="text-sm">Your credit usage is optimized</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'predictive_low_balance':
        return <AlertTriangle className="h-5 w-5 text-warning" />;
      case 'anomaly_detected':
        return <Shield className="h-5 w-5 text-destructive" />;
      case 'efficiency_opportunity':
        return <Target className="h-5 w-5 text-primary" />;
      case 'conversion_trigger':
        return <TrendingUp className="h-5 w-5 text-success" />;
      case 'fraud_alert':
        return <Shield className="h-5 w-5 text-destructive" />;
      default:
        return <Bell className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-destructive text-destructive-foreground';
      case 'high':
        return 'bg-warning text-warning-foreground';
      case 'medium':
        return 'bg-primary text-primary-foreground';
      case 'low':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleMarkAsRead = async (alertId: string) => {
    try {
      await markAlertAsRead.mutateAsync(alertId);
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
    }
  };

  const handleDismiss = async (alertId: string) => {
    try {
      await dismissAlert.mutateAsync(alertId);
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Bell className="h-5 w-5" />
          <span>Smart Alerts</span>
          <Badge variant="secondary" className="ml-auto">
            {Array.isArray(smartAlerts) ? smartAlerts.length : 0}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Array.isArray(smartAlerts) && smartAlerts.map((alert) => (
            <div 
              key={alert.id} 
              className={`p-4 border rounded-lg transition-all ${
                alert.is_read ? 'opacity-70' : 'border-l-4 border-l-primary'
              }`}
            >
              <div className="flex items-start justify-between space-x-3">
                <div className="flex items-start space-x-3 flex-1">
                  {getAlertIcon(alert.alert_type)}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground">{alert.title}</h4>
                      <Badge className={getSeverityColor(alert.severity)} variant="secondary">
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {alert.message}
                    </p>
                    
                    {alert.recommendation && (
                      <div className="mt-2 p-2 bg-accent rounded text-sm">
                        <p className="font-medium text-accent-foreground">Recommendation:</p>
                        <p className="text-accent-foreground/80">
                          {typeof alert.recommendation === 'string' 
                            ? alert.recommendation 
                            : alert.recommendation.message || 'See details for more information'
                          }
                        </p>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  {!alert.is_read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkAsRead(alert.id)}
                      disabled={markAlertAsRead.isPending}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDismiss(alert.id)}
                    disabled={dismissAlert.isPending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {alert.trigger_data && (
                <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                  <details>
                    <summary className="cursor-pointer hover:text-foreground">
                      View technical details
                    </summary>
                    <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                      {JSON.stringify(alert.trigger_data, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};